import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const StatusIndicatorPanel = ({ position = 'header' }) => {
  const [systemStatus, setSystemStatus] = useState({
    overall: 'operational',
    services: {
      api: { status: 'operational', responseTime: '45ms' },
      database: { status: 'operational', responseTime: '12ms' },
      payments: { status: 'operational', responseTime: '230ms' },
      notifications: { status: 'degraded', responseTime: '1.2s' },
      gps: { status: 'operational', responseTime: '89ms' }
    },
    metrics: {
      activeUsers: 1247,
      activeDrivers: 89,
      pendingRides: 23,
      systemLoad: 67
    }
  });

  const [showDetailedView, setShowDetailedView] = useState(false);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemStatus(prev => ({
        ...prev,
        metrics: {
          ...prev?.metrics,
          activeUsers: prev?.metrics?.activeUsers + Math.floor(Math.random() * 10 - 5),
          activeDrivers: prev?.metrics?.activeDrivers + Math.floor(Math.random() * 4 - 2),
          pendingRides: Math.max(0, prev?.metrics?.pendingRides + Math.floor(Math.random() * 6 - 3)),
          systemLoad: Math.min(100, Math.max(0, prev?.metrics?.systemLoad + Math.floor(Math.random() * 10 - 5)))
        }
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'success';
      case 'degraded': return 'warning';
      case 'outage': return 'error';
      default: return 'text-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return 'CheckCircle';
      case 'degraded': return 'AlertTriangle';
      case 'outage': return 'XCircle';
      default: return 'HelpCircle';
    }
  };

  if (position === 'header') {
    return (
      <div className="relative">
        <button
          onClick={() => setShowDetailedView(!showDetailedView)}
          className="flex items-center space-x-2 px-3 py-1 bg-success-50 rounded-full hover:bg-success-100 transition-colors duration-200"
        >
          <div className={`w-2 h-2 rounded-full animate-pulse ${
            systemStatus?.overall === 'operational' ? 'bg-success' :
            systemStatus?.overall === 'degraded'? 'bg-warning' : 'bg-error'
          }`}></div>
          <span className={`text-sm font-medium ${
            systemStatus?.overall === 'operational' ? 'text-success' :
            systemStatus?.overall === 'degraded'? 'text-warning' : 'text-error'
          }`}>
            System {systemStatus?.overall === 'operational' ? 'Online' : 
                   systemStatus?.overall === 'degraded' ? 'Degraded' : 'Offline'}
          </span>
          <Icon name="ChevronDown" size={14} />
        </button>
        {/* Detailed Status Dropdown */}
        {showDetailedView && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-surface rounded-lg shadow-modal border border-border z-1000">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-text-primary">System Status</h3>
              <p className="text-sm text-text-muted">Real-time system monitoring</p>
            </div>

            {/* Service Status */}
            <div className="p-4 space-y-3">
              <h4 className="text-sm font-medium text-text-primary">Services</h4>
              {Object.entries(systemStatus?.services)?.map(([service, data]) => (
                <div key={service} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={getStatusIcon(data?.status)} 
                      size={14} 
                      color={`var(--color-${getStatusColor(data?.status)})`}
                    />
                    <span className="text-sm capitalize text-text-primary">{service}</span>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-medium text-${getStatusColor(data?.status)}`}>
                      {data?.status}
                    </span>
                    <p className="text-xs text-text-muted font-mono">{data?.responseTime}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Key Metrics */}
            <div className="p-4 border-t border-border space-y-3">
              <h4 className="text-sm font-medium text-text-primary">Live Metrics</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-2 bg-primary-50 rounded-lg">
                  <p className="text-lg font-bold text-primary font-mono">{systemStatus?.metrics?.activeUsers}</p>
                  <p className="text-xs text-text-muted">Active Users</p>
                </div>
                <div className="text-center p-2 bg-secondary-50 rounded-lg">
                  <p className="text-lg font-bold text-secondary font-mono">{systemStatus?.metrics?.activeDrivers}</p>
                  <p className="text-xs text-text-muted">Active Drivers</p>
                </div>
                <div className="text-center p-2 bg-accent-50 rounded-lg">
                  <p className="text-lg font-bold text-primary font-mono">{systemStatus?.metrics?.pendingRides}</p>
                  <p className="text-xs text-text-muted">Pending Rides</p>
                </div>
                <div className="text-center p-2 bg-warning-50 rounded-lg">
                  <p className="text-lg font-bold text-warning font-mono">{systemStatus?.metrics?.systemLoad}%</p>
                  <p className="text-xs text-text-muted">System Load</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Sidebar version
  return (
    <div className="p-4 bg-primary-50 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-text-primary">System Status</h4>
        <button
          onClick={() => setShowDetailedView(!showDetailedView)}
          className="p-1 hover:bg-primary-100 rounded transition-colors duration-200"
        >
          <Icon name="MoreHorizontal" size={16} />
        </button>
      </div>
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${
            systemStatus?.overall === 'operational' ? 'bg-success animate-pulse' :
            systemStatus?.overall === 'degraded'? 'bg-warning' : 'bg-error'
          }`}></div>
          <span className="text-sm text-text-primary">
            {systemStatus?.overall === 'operational' ? 'All systems operational' :
             systemStatus?.overall === 'degraded'? 'Some services degraded' : 'System issues detected'}
          </span>
        </div>

        {showDetailedView && (
          <div className="mt-3 space-y-2 animate-fade-in">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-text-muted">Users:</span>
                <span className="ml-1 font-mono text-text-primary">{systemStatus?.metrics?.activeUsers}</span>
              </div>
              <div>
                <span className="text-text-muted">Drivers:</span>
                <span className="ml-1 font-mono text-text-primary">{systemStatus?.metrics?.activeDrivers}</span>
              </div>
              <div>
                <span className="text-text-muted">Pending:</span>
                <span className="ml-1 font-mono text-text-primary">{systemStatus?.metrics?.pendingRides}</span>
              </div>
              <div>
                <span className="text-text-muted">Load:</span>
                <span className="ml-1 font-mono text-text-primary">{systemStatus?.metrics?.systemLoad}%</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusIndicatorPanel;